import React, { useState, useEffect, useActionState } from 'react';
import { api } from '../api/client.js';
import { PROTOTYPE_PERSONAS } from '../data/prototypePersonas.js';

function NewPersonaModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('private');

  const [, submit, isPending] = useActionState(async () => {
    await onCreate({ name, visibility, profile: {} });
    onClose();
  }, null);

  return (
    <div className="cb-modal-backdrop" role="dialog" aria-modal="true" aria-label="Create persona">
      <div className="cb-modal">
        <h2>New Persona</h2>
        <div className="cb-field">
          <label htmlFor="persona-name">Name</label>
          <input id="persona-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="cb-field">
          <label htmlFor="persona-visibility">Visibility</label>
          <select id="persona-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
            <option value="private">Private</option>
            <option value="team">Team</option>
            <option value="org">Organization</option>
          </select>
        </div>
        <div className="cb-row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="cb-btn sm" onClick={onClose}>Cancel</button>
          <button className="cb-btn primary sm" onClick={submit} disabled={isPending || !name}>
            {isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PersonasView({ navigate }) {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    api.listPersonas()
      .then((data) => setPersonas(Array.isArray(data) ? data : data.items ?? data.personas ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (p) => {
    await api.createPersona(p);
    load();
  };

  const [seedState, seedPrototypes, isSeeding] = useActionState(async () => {
    const existing = new Set(personas.map((p) => p.name));
    const toCreate = PROTOTYPE_PERSONAS.filter((p) => !existing.has(p.name));
    if (toCreate.length === 0) return { message: 'All prototype personas already exist.' };
    const results = await Promise.allSettled(toCreate.map((p) => api.createPersona(p)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    load();
    return { message: `Created ${toCreate.length - failed} of ${toCreate.length} personas.` };
  }, null);

  const handleRowClick = (persona) => {
    const params = new URLSearchParams({ personaId: persona.id });
    navigate(`/?${params}`);
  };

  return (
    <>
      {showNew && <NewPersonaModal onClose={() => setShowNew(false)} onCreate={handleCreate} />}
      <div className="cb-row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Persona Library</h1>
        <div className="cb-row">
          <button className="cb-btn sm" onClick={seedPrototypes} disabled={isSeeding}>
            {isSeeding ? 'Seeding…' : 'Seed prototype personas'}
          </button>
          <button className="cb-btn primary sm" onClick={() => setShowNew(true)}>+ New persona</button>
        </div>
      </div>

      {seedState?.message && (
        <div className="cb-alert" style={{ background: 'var(--cb-blue-bg)', borderColor: 'var(--cb-blue)', color: 'var(--cb-blue)' }}>
          {seedState.message}
        </div>
      )}

      {error && <div className="cb-alert" role="alert">{error}</div>}

      {loading ? (
        <div className="cb-empty">Loading…</div>
      ) : personas.length === 0 ? (
        <div className="cb-empty">No personas found. Create one to get started.</div>
      ) : (
        <div className="cb-card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="cb-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Owner</th>
                <th>Visibility</th>
                <th>Last modified</th>
              </tr>
            </thead>
            <tbody>
              {personas.map((p) => (
                <tr key={p.id} onClick={() => handleRowClick(p)} style={{ cursor: 'pointer' }} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(p); }}>
                  <td style={{ color: 'var(--cb-blue-4)', fontWeight: 500 }}>{p.name}</td>
                  <td>{p.owner ?? '—'}</td>
                  <td><span className={`cb-badge ${p.visibility === 'org' ? 'success' : 'blue'}`}>{p.visibility ?? 'private'}</span></td>
                  <td className="cb-muted">{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
